import { eventDetailUrl, parseEventDetailPath } from '../../shared/event-url';
import { useBranding } from '../branding';
import { calendarReturnTo } from '../navigation-state';
import '../components/event-usability.css';
import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import type { Calendar, CalendarEvent } from '../../shared/contracts';
import { api, type ClientSession } from '../api';
import { EventParticipation } from '../components/EventParticipation';
import { EventArticle } from '../components/EventArticle';
import { ErrorMessage, Loading } from '../components/Status';
import { EventPlacementDialog } from '../components/EventPlacementDialog';

const EventEditor = lazy(() =>
  import('../components/EventEditor').then((module) => ({ default: module.EventEditor })),
);
export function EventPage({ path, session }: { path: string; session: ClientSession | null }) {
  const { config } = useBranding();
  const actions = useRef<HTMLDetailsElement>(null);
  useEffect(() => {
    const dismiss = (event: PointerEvent) => {
      if (!actions.current?.contains(event.target as Node))
        actions.current?.removeAttribute('open');
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && actions.current?.open) {
        actions.current.removeAttribute('open');
        actions.current.querySelector('summary')?.focus();
      }
    };
    document.addEventListener('pointerdown', dismiss);
    document.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('pointerdown', dismiss);
      document.removeEventListener('keydown', escape);
    };
  }, []);
  const query = new URLSearchParams(window.location.search);
  const detail = parseEventDetailPath(path);
  const occurrence = detail.occurrence || query.get('occurrence');
  const returnTo = calendarReturnTo();
  const [event, setEvent] = useState<CalendarEvent | null>(null);
  const [error, setError] = useState('');
  const [actionError, setActionError] = useState('');
  const [calendars, setCalendars] = useState<Calendar[]>([]);
  const [editorMode, setEditorMode] = useState<'edit' | 'duplicate' | null>(null);
  const [editorEvent, setEditorEvent] = useState<CalendarEvent | null>(null);
  const [seriesEvent, setSeriesEvent] = useState<CalendarEvent>();
  const [placementOpen, setPlacementOpen] = useState(false);
  const [participationRevision, setParticipationRevision] = useState(0);
  useEffect(() => {
    if (session?.user)
      void api
        .calendars()
        .then((result) => setCalendars(result.calendars))
        .catch(() => setCalendars([]));
  }, [session?.user?.id]);
  useEffect(() => {
    let current = true;
    setEvent(null);
    setError('');
    api
      .eventPage(detail.path, occurrence)
      .then(({ event: value }) => {
        if (current) {
          setEvent(value);
          const canonical = eventDetailUrl(value.path, occurrence);
          if (window.location.pathname + window.location.search !== canonical) {
            window.history.replaceState(null, '', canonical);
          }
        }
      })
      .catch((cause) => {
        if (current) setError(cause instanceof Error ? cause.message : 'Could not load the event');
      });
    return () => {
      current = false;
    };
  }, [path, detail.path, occurrence]);
  if (error)
    return (
      <main id="main-content" className="event-page">
        <ErrorMessage message={error} />
      </main>
    );
  if (!event)
    return (
      <main id="main-content" className="event-page">
        <Loading label="Loading event" />
      </main>
    );
  const accent = event.page.accentColor || '#d9693f';
  const background = event.page.backgroundColor || '#fffaf0';
  const editable = Boolean(
    session?.user &&
    calendars.some(
      (calendar) =>
        calendar.id === event.calendarId && calendar.canEdit && calendar.source !== 'replicated',
    ),
  );
  const openEditor = async (mode: 'edit' | 'duplicate') => {
    actions.current?.removeAttribute('open');
    setActionError('');
    try {
      const [{ event: selected }, series] = await Promise.all([
        api.event(event.id, occurrence || undefined),
        mode === 'edit' && occurrence ? api.event(event.id) : Promise.resolve(null),
      ]);
      setEditorEvent(selected);
      setSeriesEvent(series?.event);
      setEditorMode(mode);
    } catch (cause) {
      setActionError(cause instanceof Error ? cause.message : 'Could not open the event editor');
    }
  };
  return (
    <main
      id="main-content"
      className="event-page"
      style={{ '--event-accent': accent, '--event-bg': background } as React.CSSProperties}
    >
      <a className="back-link" href={returnTo}>
        ← {config.eventBackLabel}
      </a>
      <details className="event-actions-menu" ref={actions}>
        <summary aria-label="Event actions">⋯</summary>
        <div className="event-actions-popover">
          {editable && (
            <>
              <button
                type="button"
                className="button button-quiet"
                onClick={() => void openEditor('edit')}
              >
                Edit event
              </button>
              <button
                type="button"
                className="button button-quiet"
                onClick={() => void openEditor('duplicate')}
              >
                Duplicate
              </button>
              <button
                type="button"
                className="button button-quiet"
                onClick={() => {
                  actions.current?.removeAttribute('open');
                  setPlacementOpen(true);
                }}
              >
                Manage calendars
              </button>
            </>
          )}
          <button
            type="button"
            className="button button-quiet"
            onClick={() => {
              actions.current?.removeAttribute('open');
              window.print();
            }}
          >
            Print event details
          </button>
        </div>
      </details>
      {actionError && <ErrorMessage message={actionError} />}
      <EventArticle event={event} />
      <EventParticipation
        key={`${event.id}:${participationRevision}`}
        eventId={event.id}
        occurrence={occurrence}
        session={session}
      />
      {editorMode && editorEvent && session && (
        <Suspense fallback={<Loading label="Opening event editor" />}>
          <EventEditor
            calendars={calendars}
            {...(editorMode === 'edit' ? { event: editorEvent } : { initialEvent: editorEvent })}
            {...(editorMode === 'edit' && seriesEvent ? { seriesEvent } : {})}
            {...(editorMode === 'edit' && occurrence ? { recurrenceId: occurrence } : {})}
            session={session}
            onClose={() => {
              setEditorMode(null);
              setEditorEvent(null);
              setSeriesEvent(undefined);
              setParticipationRevision((value) => value + 1);
            }}
            onSaved={(saved) => {
              if (editorMode === 'duplicate') {
                window.location.assign(eventDetailUrl(saved.path));
                return;
              }
              setEvent(saved);
              setEditorMode(null);
              setEditorEvent(null);
              setSeriesEvent(undefined);
              setParticipationRevision((value) => value + 1);
            }}
          />
        </Suspense>
      )}
      {placementOpen && session && (
        <EventPlacementDialog
          eventId={event.id}
          calendars={calendars}
          csrfToken={session.csrfToken}
          onClose={() => setPlacementOpen(false)}
          onSaved={() => setPlacementOpen(false)}
        />
      )}
    </main>
  );
}
